import {
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';

import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';

/**
 * Bridge directive that wires up ng-bootstrap's NgbTypeahead
 * using the legacy search-object pattern:
 *
 *   {
 *     query:  (typed: string) => Promise<any[]>,
 *     format: (item: any) => string,
 *     choose: (item: any) => void
 *   }
 *
 * Usage:  <input [worTypeahead]="searchObj" [(ngModel)]="searchObj.selected" />
 */
@Directive({
  selector: '[worTypeahead]',
  exportAs: 'worTypeahead'
})
export class TypeaheadDirective implements OnInit, OnDestroy {
  @Input('worTypeahead') config: any;

  private dropdown: HTMLElement;
  private results: any[] = [];
  private input$ = new Subject<string>();
  private subscription: Subscription;
  private selectedIndex = -1;
  private isOpen = false;

  constructor (
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit () {
    const inputEl = this.el.nativeElement as HTMLInputElement;

    // Create dropdown container
    this.dropdown = this.renderer.createElement('div');
    this.renderer.addClass(this.dropdown, 'dropdown-menu');
    this.renderer.setStyle(this.dropdown, 'width', '100%');
    this.renderer.setStyle(this.dropdown, 'max-height', '300px');
    this.renderer.setStyle(this.dropdown, 'overflow-y', 'auto');

    // Position relative wrapper
    const parent = inputEl.parentElement;
    if (parent) {
      this.renderer.setStyle(parent, 'position', 'relative');
      this.renderer.appendChild(parent, this.dropdown);
    }

    // Listen for input
    this.renderer.listen(inputEl, 'input', (event: Event) => {
      const value = (event.target as HTMLInputElement).value;
      this.input$.next(value);
    });

    // Listen for keydown (arrow navigation + enter + escape)
    this.renderer.listen(inputEl, 'keydown', (event: KeyboardEvent) => {
      if (!this.isOpen) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.results.length - 1);
        this.highlightItem();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        this.highlightItem();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (this.selectedIndex >= 0 && this.selectedIndex < this.results.length) {
          this.selectItem(this.results[this.selectedIndex]);
        }
      } else if (event.key === 'Escape') {
        this.closeDropdown();
      }
    });

    // Listen for blur to close dropdown (with delay for click)
    this.renderer.listen(inputEl, 'blur', () => {
      setTimeout(() => this.closeDropdown(), 200);
    });

    // Debounced search
    this.subscription = this.input$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (!query || query.length < 1 || !this.config?.query) {
          return of([]);
        }
        const result = this.config.query(query);
        return result instanceof Promise ? result.then(r => r || []) : of(result || []);
      })
    ).subscribe((results: any[]) => {
      this.results = Array.isArray(results) ? results : [];
      this.selectedIndex = -1;
      this.renderDropdown();
    });
  }

  ngOnDestroy () {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.dropdown?.parentElement) {
      this.dropdown.parentElement.removeChild(this.dropdown);
    }
  }

  private renderDropdown () {
    // Clear existing items
    this.dropdown.innerHTML = '';

    if (!this.results.length) {
      this.closeDropdown();
      return;
    }

    this.results.forEach((item, index) => {
      const li = this.renderer.createElement('a');
      this.renderer.addClass(li, 'dropdown-item');
      this.renderer.setStyle(li, 'cursor', 'pointer');
      this.renderer.setStyle(li, 'padding', '6px 12px');
      this.renderer.setStyle(li, 'display', 'block');

      const label = this.formatItem(item);
      li.textContent = label;

      this.renderer.listen(li, 'mousedown', (e: Event) => {
        e.preventDefault();
        this.selectItem(item);
      });

      this.renderer.appendChild(this.dropdown, li);
    });

    this.renderer.addClass(this.dropdown, 'show');
    this.isOpen = true;
  }

  private formatItem (item: any): string {
    if (this.config?.format) {
      try { return this.config.format(item) || ''; }
      catch { return String(item); }
    }
    if (typeof item === 'string') return item;
    return item?.display_name || item?.name || item?.item_code || item?.description || String(item);
  }

  private selectItem (item: any) {
    const inputEl = this.el.nativeElement as HTMLInputElement;
    const label = this.formatItem(item);
    inputEl.value = label;

    // Trigger ngModel update
    inputEl.dispatchEvent(new Event('input'));

    if (this.config?.choose) {
      this.config.choose(item);
    }

    this.closeDropdown();
  }

  private highlightItem () {
    const items = this.dropdown.querySelectorAll('.dropdown-item');
    items.forEach((el, i) => {
      if (i === this.selectedIndex) {
        (el as HTMLElement).style.backgroundColor = '#e9ecef';
      } else {
        (el as HTMLElement).style.backgroundColor = '';
      }
    });
  }

  private closeDropdown () {
    this.renderer.removeClass(this.dropdown, 'show');
    this.isOpen = false;
    this.results = [];
  }
}
